import { ApiError, getAccessToken } from '@/api/http';

const API_BASE = process.env.REACT_APP_API_URL ?? '';
const ENV_API_KEY = process.env.REACT_APP_API_KEY ?? '';

export type ExportFormat = 'csv' | 'json';

export type ExportParamValue = string | number | boolean | undefined | null;

/** Query params for export endpoints (accepts typed filter objects from list APIs). */
export type ExportParams = { [key: string]: ExportParamValue };

function buildAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else if (ENV_API_KEY) {
    headers['X-API-Key'] = ENV_API_KEY;
  }
  return headers;
}

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const match = /filename="([^"]+)"/.exec(header);
  return match?.[1] ?? fallback;
}

export async function downloadExport(
  path: string,
  params: ExportParams | object,
  format: ExportFormat
): Promise<string> {
  const query = new URLSearchParams({ fmt: format });
  Object.entries(params as ExportParams).forEach(([key, value]) => {
    if (value != null && value !== '') {
      query.set(key, String(value));
    }
  });

  const res = await fetch(`${API_BASE}/api${path}?${query.toString()}`, {
    headers: buildAuthHeaders(),
  });

  if (!res.ok) {
    let detail: unknown = res.statusText;
    try {
      const body = await res.json();
      detail = body.detail ?? body;
    } catch {
      /* ignore */
    }
    const message =
      typeof detail === 'object' && detail !== null && 'message' in detail
        ? String((detail as { message: string }).message)
        : String(detail);
    throw new ApiError(message, res.status, detail);
  }

  const blob = await res.blob();
  const filename = filenameFromDisposition(
    res.headers.get('Content-Disposition'),
    `export-${Date.now()}.${format}`
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
  return filename;
}
